/* Serial Seal — client-side seal renderer (beta).
 * Mirrors the VialArt buildLabel architecture: templates are SVG strings with
 * {{tokens}}, substituted + mounted here. Preview == print: the same template
 * files feed Label Farm's renderer later, so nothing here may diverge from
 * the token conventions ({{brand}} {{serial}} {{run_total}} {{qr}}
 * {{color_hex}}, data-fit, data-qr, .ss-mark).
 */
window.SS = (function () {
  'use strict';
  var _n = 0;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function hex(c, fallback) {
    c = String(c || '').trim();
    return /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(c) ? c : (fallback || '#7c4dff');
  }

  /* ——— holo-safe ink clamp ———
   * The stock is silver holographic foil with no white ink: pale or pastel
   * ink lays down almost nothing and vanishes into the foil. Every accent —
   * swatch pick, design default, reorder record — passes through here so
   * nothing lighter than a deep ink ever reaches preview or print. Greys
   * (low saturation) are deepened without inventing a hue. */
  function inkSafe(c) {
    var h6 = hex(c);
    if (h6.length === 4) h6 = '#' + h6[1] + h6[1] + h6[2] + h6[2] + h6[3] + h6[3];
    var r = parseInt(h6.slice(1, 3), 16) / 255,
        g = parseInt(h6.slice(3, 5), 16) / 255,
        b = parseInt(h6.slice(5, 7), 16) / 255;
    var mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
    if (l <= 0.45) return h6;
    var d = mx - mn, s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1)), hu = 0;
    if (d !== 0) {
      if (mx === r) hu = ((g - b) / d) % 6;
      else if (mx === g) hu = (b - r) / d + 2;
      else hu = (r - g) / d + 4;
      hu = (hu * 60 + 360) % 360;
    }
    var L = 0.40, S = s < 0.15 ? s : Math.max(s, 0.55);
    var C = (1 - Math.abs(2 * L - 1)) * S,
        X = C * (1 - Math.abs(((hu / 60) % 2) - 1)),
        m = L - C / 2, rr = 0, gg = 0, bb = 0;
    if (hu < 60) { rr = C; gg = X; } else if (hu < 120) { rr = X; gg = C; }
    else if (hu < 180) { gg = C; bb = X; } else if (hu < 240) { gg = X; bb = C; }
    else if (hu < 300) { rr = X; bb = C; } else { rr = C; bb = X; }
    function h2(v) { v = Math.round((v + m) * 255); return (v < 16 ? '0' : '') + v.toString(16); }
    return '#' + h2(rr) + h2(gg) + h2(bb);
  }

  /* ——— random serials ———
   * Every label carries its own random serial so no two customers ever hold
   * the same number (sequential packs gave everyone 001–012). 24-char alphabet
   * with the lookalikes removed (0/O/Q, 1/I/L, 5/S, 8/B, 2/Z) — these print at
   * 2.2–2.9mm and get read out over the phone in dispute cases, so every glyph
   * must survive both. 24^6 = 191M combinations; per-order uniqueness is
   * enforced with a Set, cross-order collisions are lottery-odds and harmless
   * (different brand artwork on the seal disambiguates).
   */
  var SAFE_ALPHA = 'ACDEFGHJKMNPRTUVWXY34679';
  function _randChars(n) {
    var out = '';
    var c = window.crypto || window.msCrypto;
    if (c && c.getRandomValues) {
      var buf = new Uint32Array(n);
      c.getRandomValues(buf);
      for (var i = 0; i < n; i++) out += SAFE_ALPHA[buf[i] % SAFE_ALPHA.length];
    } else {
      for (var j = 0; j < n; j++) out += SAFE_ALPHA[Math.floor(Math.random() * SAFE_ALPHA.length)];
    }
    return out;
  }
  function randSerial() {
    var body = _randChars(6);
    return body.slice(0, 3) + '-' + body.slice(3);
  }

  function tpl(key) {
    var list = window.SS_TEMPLATES || [];
    for (var i = 0; i < list.length; i++) if (list[i].key === key) return list[i];
    return list[0] || null;
  }

  // deterministic PRNG from string — QR placeholder must be stable per URL
  function seedFrom(str) {
    var h = 0x811c9dc5; str = String(str || '');
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193); }
    return h >>> 0;
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Namespace ids per mount. Two mounts of the SAME design (gallery card and
  // main preview) would otherwise share clipPath/gradient ids, and the second
  // one silently steals the first's — clipped-away artwork, blank seals.
  function nsIds(svg) {
    var n = ++_n;
    svg = svg.replace(/\bid="([^"]+)"/g, function (m, p) { return 'id="' + p + '_i' + n + '"'; });
    svg = svg.replace(/url\(#([^)]+)\)/g, function (m, p) { return 'url(#' + p + '_i' + n + ')'; });
    svg = svg.replace(/href="#([^"]+)"/g, function (m, p) { return 'href="#' + p + '_i' + n + '"'; });
    return svg;
  }

  function substitute(t, f) {
    var svg = t.svg || '';
    svg = svg.split('{{brand}}').join(esc(f.brand || (t.defaults && t.defaults.brand) || 'YOUR BRAND'));
    /* 'none' is the no-serial sentinel (plain identical stickers) — the slot
       substitutes to nothing rather than stamping the word "none" on the seal */
    svg = svg.split('{{serial}}').join(f.serial === 'none' ? '' : esc(f.serial || '001'));
    svg = svg.split('{{run_total}}').join(esc(f.run_total || '500'));
    svg = svg.split('{{qr}}').join(esc(f.qr || ''));
    svg = svg.split('{{color_hex}}').join(inkSafe(f.color || (t.defaults && t.defaults.color)));
    return svg;
  }

  /* REAL QR encoding — window.SS_QRLIB is the theme's QR library, loaded as a
     module by any page that renders seals (see the module shim in the builder
     / cart / add-logo pages). When it is present every preview AND every
     printed seal carries a genuinely scannable code, so the density the
     customer sees is the density that prints. Without it we fall back to the
     seeded placeholder pattern rather than rendering nothing. */
  function qrMatrix(text) {
    if (!window.SS_QRLIB || !text) return null;
    try {
      var holder = document.createElement('div');
      var qc = new window.SS_QRLIB(holder, { text: String(text), useSVG: true });
      var model = qc._oQRCode;
      if (!model || typeof model.getModuleCount !== 'function') return null;
      var n = model.getModuleCount(), dark = [];
      for (var row = 0; row < n; row++) {
        for (var col = 0; col < n; col++) {
          if (model.isDark(row, col)) dark.push([col, row]);
        }
      }
      return dark.length ? { n: n, dark: dark } : null;
    } catch (_) { return null; }
  }

  // Draws into rect[data-qr]. Real code when the encoder is available.
  function drawQr(root) {
    var rects = root.querySelectorAll('rect[data-qr]');
    for (var r = 0; r < rects.length; r++) {
      var rect = rects[r];
      var data = rect.getAttribute('data-qr') || '';
      var x = parseFloat(rect.getAttribute('x')), y = parseFloat(rect.getAttribute('y'));
      var w = parseFloat(rect.getAttribute('width')), h = parseFloat(rect.getAttribute('height'));
      if (!(w > 0 && h > 0)) continue;
      var real = qrMatrix(data);
      var N = real ? real.n : 21, m = w / (N + 2); // one-module quiet zone
      var g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      if (real) { g.setAttribute('data-qr-real', '1'); }
      var dark = real ? real.dark : [];
      if (!real) {
        var rand = mulberry32(seedFrom(data || 'serialseal'));
        var finder = function (fx, fy) {
          for (var i = 0; i < 7; i++) for (var j = 0; j < 7; j++) {
            var ring = (i === 0 || i === 6 || j === 0 || j === 6);
            var core = (i >= 2 && i <= 4 && j >= 2 && j <= 4);
            if (ring || core) dark.push([fx + i, fy + j]);
          }
        };
        finder(0, 0); finder(N - 7, 0); finder(0, N - 7);
        for (var t = 8; t < N - 8; t++) if (t % 2 === 0) { dark.push([t, 6]); dark.push([6, t]); }
        for (var i = 0; i < N; i++) for (var j = 0; j < N; j++) {
          var inFinder = (i < 8 && j < 8) || (i >= N - 8 && j < 8) || (i < 8 && j >= N - 8);
          if (inFinder || i === 6 || j === 6) continue;
          if (rand() < 0.46) dark.push([i, j]);
        }
      }
      for (var k = 0; k < dark.length; k++) {
        var sq = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        sq.setAttribute('x', (x + m + dark[k][0] * m).toFixed(3));
        sq.setAttribute('y', (y + m + dark[k][1] * m).toFixed(3));
        sq.setAttribute('width', (m * 1.04).toFixed(3));
        sq.setAttribute('height', (m * 1.04).toFixed(3));
        sq.setAttribute('fill', '#111116');
        g.appendChild(sq);
      }
      /* tag the group so a later redraw can replace it rather than stack a
         second QR on top of the placeholder */
      g.setAttribute('data-qr-drawn', '1');
      var prev = rect.parentNode.querySelector(':scope > g[data-qr-drawn]');
      if (prev && prev !== g) prev.parentNode.removeChild(prev);
      rect.parentNode.insertBefore(g, rect.nextSibling);
    }
  }

  /* The encoder is an ES module and lands AFTER the first seals are painted,
     so anything drawn with the placeholder is redrawn for real the moment it
     is available. Without this the cart keeps the unscannable pattern it
     painted on load (owner 2026-08-05). */
  document.addEventListener('ss-qr-ready', function () {
    try {
      var seals = document.querySelectorAll('.ss-seal svg');
      for (var i = 0; i < seals.length; i++) drawQr(seals[i]);
    } catch (_) { }
  });

  // Microtext is deliberately built slightly too long, then trimmed here using
  // real glyph metrics. Shrinking the font instead would push it under the
  // ~0.45mm print floor where it turns to mush on vinyl, so we drop characters.
  function trimMicro(root) {
    var els = root.querySelectorAll('text[data-micro]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i], max = parseFloat(el.getAttribute('data-micro'));
      if (!(max > 0)) continue;
      var txt = el.textContent, guard = 0;
      try {
        while (txt.length > 8 && el.getComputedTextLength() > max && guard++ < 400) {
          txt = txt.slice(0, -1);
          el.textContent = txt;
        }
        /* never end on a partial repeat — "SERIALSEAL.COM · SER" reads as
           a defect at legible sizes. Cut back to the last complete unit. */
        var sep = el.getAttribute('data-micro-sep');
        if (sep) {
          var li = txt.lastIndexOf(sep);
          if (li > 0) {
            var unitLen = txt.indexOf(sep);
            var tail = txt.slice(li + sep.length).trim();
            if (unitLen > 0 && tail.length < unitLen) {
              txt = txt.slice(0, li).trim();
              el.textContent = txt;
            }
          }
        }
      } catch (_) {}
    }
  }

  // Auto-fit: shrink any text[data-fit] whose rendered width exceeds its cap.
  // Uses real glyph metrics (getComputedTextLength) — element must be in the
  // live DOM and visible, else length reads 0 and we skip.
  function fit(root) {
    var texts = root.querySelectorAll('text[data-fit]');
    for (var i = 0; i < texts.length; i++) {
      var el = texts[i], max = parseFloat(el.getAttribute('data-fit'));
      if (!(max > 0)) continue;
      try {
        // Letter-spacing does not scale with font-size, so a spaced-out
        // caption stays too wide however far the size drops. Scale both, and
        // iterate because the measurement moves as we go.
        var fs0 = parseFloat(el.getAttribute('font-size')) || 3;
        var floorMm = Math.min(1.4, fs0);
        var hard = el.hasAttribute('data-fit-hard');
        for (var pass = 0; pass < 4; pass++) {
          var len = el.getComputedTextLength();
          if (!(len > 0) || len <= max) break;
          var fs = parseFloat(el.getAttribute('font-size')) || 3;
          // VialArt parity: the production pipeline on the SAME press floors
          // fitted type at 1.4mm "so extreme names stay legible rather than
          // shrinking to nothing" (_fit_font_size, label_renderer.py). Three
          // Serial Seal amendments for the tighter die: never floor ABOVE the
          // authored size (growing an overflowing 1.0mm caption was an old
          // bug here); ACCEPT the floor when its overflow stays within what
          // the margins absorb (≤18% over budget); past that, the final pass
          // fits exactly — type at 0.6mm is small; type past the edge is gone.
          // data-fit-hard: the cap was derived from the distance to the die
          // edge, so there is no margin left for the tolerance to spend —
          // fit exactly or the glyphs cross the cut line.
          if (!hard && fs <= floorMm + 0.01 && len <= max * 1.18) break;
          var ratio = max / len;
          var ls = parseFloat(el.getAttribute('letter-spacing')) || 0;
          var floor = pass < 3 ? floorMm : 0.55;
          el.setAttribute('font-size', Math.max(floor, fs * ratio).toFixed(2));
          if (ls) el.setAttribute('letter-spacing', Math.max(0, ls * ratio).toFixed(3));
        }
      } catch (_) {}
    }
  }

  /* Logos arrive with transparent padding baked around the artwork, and a
     contain-fit renders that padding as dead space — the mark never fills its
     slot. Crop to the alpha bounding box first, so "full width" means the
     VISIBLE art, not the file's margins. Cached per data URI; falls back to
     the uncropped image on any failure (cross-origin taint, decode error). */
  var _cropCache = {};
  /* cb(croppedUri, photoLike) — photoLike means >=92% of the pixels inside
     the ink bbox are opaque: a photograph or a flattened scan, safe to
     cover-crop. A transparent logo mark is NOT (pokebrit 2026-08-19:
     cover-crop cut their ribbon wordmark — "my logo is not fitting"). */
  function croppedLogo(dataUri, cb) {
    var key = dataUri.length + ':' + dataUri.slice(30, 90);
    if (_cropCache[key]) return cb(_cropCache[key].uri, _cropCache[key].photo);
    var im = new Image();
    if (/^https?:/i.test(dataUri)) im.crossOrigin = 'anonymous';
    im.onload = function () {
      try {
        var cv = document.createElement('canvas');
        var s = Math.min(900 / im.width, 900 / im.height, 1);
        cv.width = Math.max(1, Math.round(im.width * s));
        cv.height = Math.max(1, Math.round(im.height * s));
        var cx = cv.getContext('2d', { willReadFrequently: true });
        cx.drawImage(im, 0, 0, cv.width, cv.height);
        var d = cx.getImageData(0, 0, cv.width, cv.height).data;
        var minX = cv.width, minY = cv.height, maxX = -1, maxY = -1, solid = 0;
        for (var y = 0; y < cv.height; y++) {
          for (var x = 0; x < cv.width; x++) {
            var al = d[(y * cv.width + x) * 4 + 3];
            if (al > 128) solid++;
            if (al > 24) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }
        if (maxX <= minX || maxY <= minY) {
          _cropCache[key] = { uri: dataUri, photo: false };
          return cb(dataUri, false);
        }
        var photo = solid >= (maxX - minX + 1) * (maxY - minY + 1) * 0.92;
        var pad = 2;
        minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
        maxX = Math.min(cv.width - 1, maxX + pad); maxY = Math.min(cv.height - 1, maxY + pad);
        var w = maxX - minX + 1, h = maxY - minY + 1;
        if (w > cv.width * 0.94 && h > cv.height * 0.94) {
          _cropCache[key] = { uri: dataUri, photo: photo };   // already tight
          return cb(dataUri, photo);
        }
        var oc = document.createElement('canvas');
        oc.width = w; oc.height = h;
        oc.getContext('2d').drawImage(cv, minX, minY, w, h, 0, 0, w, h);
        var out = oc.toDataURL('image/png');
        _cropCache[key] = { uri: out, photo: photo };
        cb(out, photo);
      } catch (_) { cb(dataUri, false); }
    };
    im.onerror = function () { cb(dataUri, false); };
    im.src = dataUri;
  }

  // Drop an uploaded logo (data URI or URL) into the template's .ss-mark slot.
  function injectLogo(root, dataUri) {
    /* EVERY anchor gets the logo (owner 2026-08-30: the duo layout carries
       one anchor per half, and only the first was served - the second half
       printed logoless). One-anchor layouts behave exactly as before. */
    var marks = root.querySelectorAll('.ss-mark');
    for (var mi = 0; mi < marks.length; mi++) injectLogoOne(marks[mi], dataUri);
  }
  function injectLogoOne(mark, dataUri) {
    if (!mark || !dataUri) return;
    var bb;
    try { bb = mark.getBBox(); } catch (_) { return; }
    if (!(bb && bb.width > 0)) return;
    var img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    img.setAttribute('x', bb.x); img.setAttribute('y', bb.y);
    img.setAttribute('width', bb.width); img.setAttribute('height', bb.height);
    /* data-cover windows: PHOTOS fill the slot edge to edge (cropping
       overflow — ss1025's card photo, owner 2026-08-18); transparent LOGO
       MARKS contain-fit so their ink is never cropped (pokebrit 2026-08-19:
       "my logo is not fitting into the box"). croppedLogo makes the call
       from the pixels; until it reports, everything starts contained. */
    var cover = mark.getAttribute('data-cover') === '1';
    img.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    function goCover() {
      var NS = 'http://www.w3.org/2000/svg';
      var svgRoot = mark.ownerSVGElement;
      if (!svgRoot) return;
      var cpId = 'lgcov' + Math.random().toString(36).slice(2, 9);
      var cp = document.createElementNS(NS, 'clipPath');
      cp.setAttribute('id', cpId);
      var cr = document.createElementNS(NS, 'rect');
      cr.setAttribute('x', bb.x); cr.setAttribute('y', bb.y);
      cr.setAttribute('width', bb.width); cr.setAttribute('height', bb.height);
      cr.setAttribute('rx', '0.5');
      cp.appendChild(cr);
      var dfs = svgRoot.querySelector('defs');
      if (!dfs) {
        dfs = document.createElementNS(NS, 'defs');
        svgRoot.insertBefore(dfs, svgRoot.firstChild);
      }
      dfs.appendChild(cp);
      img.setAttribute('clip-path', 'url(#' + cpId + ')');
      img.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    }
    img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', dataUri);
    img.setAttribute('href', dataUri);
    mark.setAttribute('opacity', '0');
    mark.parentNode.insertBefore(img, mark.nextSibling);
    /* swap in the bbox-cropped version as soon as it's ready, and only
       then decide cover vs contain — from the pixels, not the box */
    croppedLogo(dataUri, function (cropped, photo) {
      if (cropped !== dataUri) {
        img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', cropped);
        img.setAttribute('href', cropped);
      }
      if (cover && photo) goCover();
    });
  }

  // Mount a template into a .ss-seal container. fields:
  // {brand, serial, run_total, qr, color, logo(dataURI)}
  // q = quality: 1 for the main preview and print, ~0.45 for gallery thumbs
  // (the guilloche engine emits thousands of points — full quality × 30 cards
  //  would bury the DOM).
  function mount(el, t, f, q) {
    if (!el || !t) return null;
    f = f || {};
    /* the builder's selected label size reaches EVERY mount - gallery tiles,
       slideshow, minis - without each call site knowing about sizes. Pages
       without the builder state (print sidecar, cart) pass their own or get
       the default. (owner 2026-08-29) */
    if (!f.label_size_id && typeof window.st === 'object' && window.st && window.st.sizeId)
      f.label_size_id = window.st.sizeId;
    // New-style portrait seals are composed by SS_SEAL; legacy ones are token
    // substitution over a static SVG string.
    var raw = (t.art !== undefined || t.plate) && window.SS_SEAL
      ? window.SS_SEAL.build(t, f, q == null ? 1 : q)
      : substitute(t, f);
    el.innerHTML = nsIds(raw);
    var svg = el.querySelector('svg');
    if (!svg) return null;
    /* holographic film overlay (VialArt storefront technique): rainbow on
       screen + pastel on overlay + travelling glint, layered OVER the art so
       the ink shimmers like it is printed on the foil. Visual only — never
       part of the SVG that reaches the print side. */
    el.insertAdjacentHTML('beforeend',
      '<div class="ss-film" aria-hidden="true"><i class="sh1"></i><i class="sh2"></i><i class="gl"></i></div>');
    /* fold guide — a DOM overlay, so it can never reach print. The compositor
       marks folded layouts on the SVG root: 'h' = horizontal crease
       (portrait), 'v' = vertical crease (landscape). Single layouts get no
       guide at all. */
    var foldAxis = svg && svg.getAttribute && svg.getAttribute('data-fold');
    if (foldAxis) {
      el.insertAdjacentHTML('beforeend',
        '<div class="ss-crease' + (foldAxis === 'v' ? ' v' : '') + '" aria-hidden="true"></div>');
    }
    fit(svg);
    trimMicro(svg);
    drawQr(svg);
    if (f.logo) injectLogo(svg, f.logo);
    return svg;
  }

  return { esc: esc, hex: hex, inkSafe: inkSafe,
           randSerial: randSerial,
           tpl: tpl, mount: mount };
})();
